import { supabase } from "@/integrations/supabase/client";

/**
 * Centralized cache for clinicas data.
 * Avoids redundant DB calls when navigating between pages.
 * Cache auto-expires after TTL_MS (5 minutes).
 */

interface ClinicaBase {
  id: string;
  nome: string;
  cidade: string;
  estado: string;
}

interface ClinicaFull extends ClinicaBase {
  responsavel: string | null;
  contato: string | null;
  valor: number | null;
  avaliacao: number | null;
  latitude: number | null;
  longitude: number | null;
  genero: string | null;
  classe: string | null;
  tipo_tratamento: string | null;
  planos_saude: string[] | null;
  link: string | null;
  endereco: string | null;
  bairro: string | null;
  cep: string | null;
  slug: string | null;
  imagens: string[] | null;
  cpf_cnpj: string | null;
  diretor_tecnico: string | null;
  crm_diretor: string | null;
  cidade_slug: string | null;
  estado_slug: string | null;
  tratamentos: string[] | null;
  created_at: string;
  updated_at: string;
}

const TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CacheEntry<T> {
  data: T[];
  timestamp: number;
}

let fullCache: CacheEntry<ClinicaFull> | null = null;
let miniCache: CacheEntry<ClinicaBase> | null = null;
let fullPromise: Promise<ClinicaFull[]> | null = null;
let miniPromise: Promise<ClinicaBase[]> | null = null;

function isValid<T>(entry: CacheEntry<T> | null): entry is CacheEntry<T> {
  return !!entry && Date.now() - entry.timestamp < TTL_MS;
}

/**
 * Fetch all clinicas with all fields (for maps, detail views).
 * Cached for 5 minutes. Concurrent calls share the same request.
 */
export async function getClinicasFull(): Promise<ClinicaFull[]> {
  if (isValid(fullCache)) return fullCache.data;

  if (fullPromise) return fullPromise;

  fullPromise = (async () => {
    try {
      const allData: ClinicaFull[] = [];
      let from = 0;
      const pageSize = 1000;

      while (true) {
        const { data } = await supabase
          .from("clinicas")
          .select("*")
          .order("nome")
          .range(from, from + pageSize - 1);
        if (!data || data.length === 0) break;
        allData.push(...(data as unknown as ClinicaFull[]));
        if (data.length < pageSize) break;
        from += pageSize;
      }

      fullCache = { data: allData, timestamp: Date.now() };
      // Also populate mini cache
      miniCache = {
        data: allData.map(({ id, nome, cidade, estado }) => ({ id, nome, cidade, estado })),
        timestamp: Date.now(),
      };
      return allData;
    } finally {
      fullPromise = null;
    }
  })();

  return fullPromise;
}

/**
 * Fetch clinicas with only id, nome, cidade, estado (for dropdowns, selects).
 * Uses the full cache if available to avoid extra requests.
 */
export async function getClinicasMini(): Promise<ClinicaBase[]> {
  if (isValid(miniCache)) return miniCache.data;
  if (isValid(fullCache)) {
    const mini = fullCache.data.map(({ id, nome, cidade, estado }) => ({ id, nome, cidade, estado }));
    miniCache = { data: mini, timestamp: Date.now() };
    return mini;
  }

  if (miniPromise) return miniPromise;

  miniPromise = (async () => {
    try {
      const { data } = await supabase
        .from("clinicas")
        .select("id, nome, cidade, estado")
        .order("nome");
      const result = (data || []) as unknown as ClinicaBase[];
      miniCache = { data: result, timestamp: Date.now() };
      return result;
    } finally {
      miniPromise = null;
    }
  })();

  return miniPromise;
}

/**
 * Invalidate all caches. Call after creating/updating/deleting a clinica.
 */
export function invalidateClinicasCache() {
  fullCache = null;
  miniCache = null;
}
